import { BTSelectItem } from "types/apiResponse/apiResponse";
import { type AccountingIntegrationType } from "types/enum";

import {
    type IEntityExtraData,
    type LinkingAction,
} from "commonComponents/entity/accounting/AccountingLinkingOptions/AccountingLinkingOptions.api.types";
import { type ContactOrgLink } from "commonComponents/entity/contact/ContactEmailAndInvite/ContactEmailAndInvite/ContactEmailAndInvite.api.types";
import { type JobsiteLinkingTypes } from "commonComponents/financial/Common/Accounting.types";

import { type JobLinkingTypes } from "entity/accounting/LinkJobToAccounting/LinkJobToAccounting.api.types";
import { type ContactEntity } from "entity/contact/Contact/Contact.api.types";

import { type default as LinkedEntityExample } from "./LinkedAccountingEntity.QBv3.api.json";

export type LinkedAccountingEntityActions = undefined | "updateAccountingEntity";

export const defaultAccountingSelectValue = -4;
export interface IAccountingEntity {
    id: number;
    displayName: string;
    entityTerm: string;
    primaryAccountingTerm: string;
    secondaryAccountingTerm: string | null;
    tertiaryAccountingTerm?: string | null;
    projectsAccountingTerm: string | null;
    jobLinkType: JobLinkingTypes | null;
    accountingIntegrationLogoImgSrc: string;
    isEntityInPendingSync: boolean;
    isEntityLinkedToAccounting: boolean;
    accountingIntegrationType: AccountingIntegrationType;
    linkingOptions: BTSelectItem<IEntityExtraData>[];
    createNewAccountingEntityOption: LinkingAction;
    primaryAccountingName: string | null;
    secondaryAccountingName: string | null;
    clientAccountingName: string | null;
    jobLinkPreference: JobLinkingTypes | null;
    primaryAccountingOptions: BTSelectItem<PrimaryAccountingOptionsExtraData>[];
    secondaryAccountingOptions: BTSelectItem<SecondaryAccountingOptionsExtraData>[];
    tertiaryAccountingOptions?: BTSelectItem[];
    externalAccountingLink?: string;
    showEntityTypeHelperText?: boolean;
}

export class AccountingEntity implements IAccountingEntity {
    constructor(id: number, data: LinkedAccountingEntityResponse) {
        this.id = id;
        this.displayName = data.accountingLinkedUser.displayName;
        this.entityTerm = data.accountingLinkedUser.entityTerm;
        this.primaryAccountingTerm = data.accountingLinkedUser.primaryAccountingTerm;
        this.secondaryAccountingTerm = data.accountingLinkedUser.secondaryAccountingTerm;
        this.tertiaryAccountingTerm = data.accountingLinkedUser.tertiaryAccountingTerm;
        this.projectsAccountingTerm = data.accountingLinkedUser.projectsAccountingTerm;
        this.primaryAccountingName = data.accountingLinkedUser.primaryAccountingName;
        this.secondaryAccountingName = data.accountingLinkedUser.secondaryAccountingName;
        this.clientAccountingName = data.accountingLinkedUser.clientAccountingName;
        this.jobLinkType = data.accountingLinkedUser.jobLinkType;
        this.accountingIntegrationLogoImgSrc =
            data.accountingLinkedUser.linkedToAccountingSystemImage;
        this.isEntityInPendingSync = data.accountingLinkedUser.isEmployeePending;
        this.isEntityLinkedToAccounting = data.accountingLinkedUser.isUserLinked;
        this.accountingIntegrationType = data.accountingLinkedUser.accountingIntegrationType;
        this.createNewAccountingEntityOption = data.createNewAccountingEntityOption;
        this.linkingOptions = data.linkingOptions;
        this.otherAccountingCostToolTipMsg = data.accountingLinkedUser
            .otherAccountingCostToolTipMsg as string;
        this.showOtherAccountingCosts = data.accountingLinkedUser.showOtherAccountingCosts;
        this.hasInvoicedOtherAccountingCosts =
            data.accountingLinkedUser.hasInvoicedOtherAccountingCosts;
        this.includeOtherAccountingCostsInBudget =
            data.accountingLinkedUser.includeOtherAccountingCostsInBudget;
        this.includeQBCostsInProjectedCostsCalculation =
            data.accountingLinkedUser.includeQBCostsInProjectedCostsCalculation;
        this.allowJobsUnderCustomer = data.accountingLinkedUser.allowJobsUnderCustomer;
        this.jobLinkPreference = data.accountingLinkedUser.jobLinkPreference;
        this.primaryAccountingOptions =
            data.primaryAccountingOptions?.map(
                (option) => new BTSelectItem<PrimaryAccountingOptionsExtraData>(option),
            ) ?? [];
        this.secondaryAccountingOptions =
            data.secondaryAccountingOptions?.map(
                (option) => new BTSelectItem<SecondaryAccountingOptionsExtraData>(option),
            ) ?? [];
        this.tertiaryAccountingOptions = data.tertiaryAccountingOptions;
        this.externalAccountingLink = data.accountingLinkedUser.externalAccountingLink;
        this.showEntityTypeHelperText = data.showEntityTypeHelperText;
        this.contacts = data.accountingLinkedUser.contacts;
    }

    id: number;
    displayName: string;
    entityTerm: string;
    primaryAccountingTerm: string;
    secondaryAccountingTerm: string | null;
    tertiaryAccountingTerm: string | null;
    projectsAccountingTerm: string | null;
    jobLinkType: JobLinkingTypes | null;
    accountingIntegrationLogoImgSrc: string;
    isEntityInPendingSync: boolean;
    isEntityLinkedToAccounting: boolean;
    accountingIntegrationType: AccountingIntegrationType;
    linkingOptions: BTSelectItem<IEntityExtraData>[];
    createNewAccountingEntityOption: LinkingAction;
    otherAccountingCostToolTipMsg: string;
    showOtherAccountingCosts: boolean;
    hasInvoicedOtherAccountingCosts: boolean;
    includeOtherAccountingCostsInBudget: boolean;
    allowJobsUnderCustomer: boolean;
    primaryAccountingName: string | null;
    secondaryAccountingName: string | null;
    clientAccountingName: string | null;
    jobLinkPreference: JobLinkingTypes | null;
    primaryAccountingOptions: BTSelectItem<PrimaryAccountingOptionsExtraData>[];
    secondaryAccountingOptions: BTSelectItem<SecondaryAccountingOptionsExtraData>[];
    tertiaryAccountingOptions: BTSelectItem[];
    externalAccountingLink?: string;
    showEntityTypeHelperText?: boolean;
    includeQBCostsInProjectedCostsCalculation: boolean;
    contacts?: BTPrimaryEntityOption[];
}

export interface ICreateUpdateAccountingResponse {
    accountingMessage: string;
    accountingSuccess: boolean;
}

export interface ICreateUnlinkAccountingResponse {
    hasInvoicedReceivables: boolean;
}

export interface ICreateAccountingValues {
    accountingEntityLinkingType: JobsiteLinkingTypes;
    createNewAccountingEntityOption: LinkingAction;
}

export class UpdateAccountingResponse {
    constructor(data?: ICreateUpdateAccountingResponse) {
        if (!data) {
            return;
        }
        this.accountingMessage = data.accountingMessage;
        this.accountingSuccess = data.accountingSuccess;
    }
    accountingMessage: string;
    accountingSuccess: boolean;
}

export class UnlinkAccountingResponse {
    constructor(data?: ICreateUnlinkAccountingResponse) {
        if (!data) {
            return;
        }
        this.hasInvoicedReceivables = data.hasInvoicedReceivables;
    }
    hasInvoicedReceivables: boolean;
}

export class AccountingValues {
    constructor(data: ICreateAccountingValues) {
        this.accountingEntityLinkingType = data.accountingEntityLinkingType;
        this.createNewAccountingEntityOption = data.createNewAccountingEntityOption;
    }

    accountingEntityLinkingType: JobsiteLinkingTypes;
    createNewAccountingEntityOption: LinkingAction;
}

export class PrimaryAccountingOptionsExtraData {
    btEntityId: string;
}

export class SecondaryAccountingOptionsExtraData {
    relatedCustomer: string;
    jobLinkType?: JobLinkingTypes;
}

export interface ICreateAccountingUserViewModel {
    displayName: string;
    accountingIntegrationType: AccountingIntegrationType;
    accountingIntegrationTypeName: string;
    accountingNameForLogin: string;
    canViewAccountingTab: boolean;
    isAccountConnected: boolean;
    isUserLinked: boolean;
    isEmployeePending: boolean;
    linkedToAccountingSystemImage: string;
    syncSubToVendorImgSrc: string;
    payrollItems?: Record<string, unknown>[] | null;
    defaultPayrollItem: string | null;
    primaryAccountingTerm: string;
    secondaryAccountingTerm: string | null;
    tertiaryAccountingTerm?: string | null;
    projectsAccountingTerm?: string | null;
    primaryAccountingName?: string | null;
    secondaryAccountingName?: string | null;
    clientAccountingName?: string | null;
    entityTerm: string;
    otherAccountingCostToolTipMsg: string | null;
    showOtherAccountingCosts: boolean;
    hasInvoicedOtherAccountingCosts?: boolean;
    includeOtherAccountingCostsInBudget: boolean;
    includeQBCostsInProjectedCostsCalculation?: boolean;
    allowJobsUnderCustomer: boolean;
    jobLinkType?: JobLinkingTypes | null;
    jobLinkPreference?: JobLinkingTypes | null;
    externalAccountingLink?: string;
    contacts?: ContactOrgLink[] | null;
}

export class BTPrimaryEntityOption {
    constructor(name: string, btEntityId?: string) {
        this.name = name;
        this.btEntityId = btEntityId;
    }
    name: string;
    btEntityId?: string;
}

export function getAccountingContactFromEntity(contact: ContactEntity) {
    return new BTPrimaryEntityOption(contact.displayName, contact.orgLinkId?.toString() ?? "0");
}

class AccountingUserViewModel {
    constructor(data: ICreateAccountingUserViewModel) {
        this.displayName = data.displayName;
        this.accountingIntegrationType = data.accountingIntegrationType;
        this.accountingIntegrationTypeName = data.accountingIntegrationTypeName;
        this.accountingNameForLogin = data.accountingNameForLogin;
        this.canViewAccountingTab = data.canViewAccountingTab;
        this.isAccountConnected = data.isAccountConnected;
        this.isUserLinked = data.isUserLinked;
        this.isEmployeePending = data.isEmployeePending;
        this.linkedToAccountingSystemImage = data.linkedToAccountingSystemImage;
        this.syncSubToVendorImgSrc = data.syncSubToVendorImgSrc;
        if (data.payrollItems) {
            this.payrollItems = data.payrollItems.map((d) => new BTSelectItem(d));
        }
        this.defaultPayrollItem = data.defaultPayrollItem;
        this.primaryAccountingTerm = data.primaryAccountingTerm;
        this.secondaryAccountingTerm = data.secondaryAccountingTerm;
        this.tertiaryAccountingTerm = data.tertiaryAccountingTerm ?? null;
        this.projectsAccountingTerm = data.projectsAccountingTerm ?? null;
        this.primaryAccountingName = data.primaryAccountingName ?? null;
        this.secondaryAccountingName = data.secondaryAccountingName ?? null;
        this.clientAccountingName = data.clientAccountingName ?? null;
        this.entityTerm = data.entityTerm;
        this.otherAccountingCostToolTipMsg = data.otherAccountingCostToolTipMsg;
        this.showOtherAccountingCosts = data.showOtherAccountingCosts;
        this.hasInvoicedOtherAccountingCosts = data.hasInvoicedOtherAccountingCosts ?? false;
        this.includeOtherAccountingCostsInBudget = data.includeOtherAccountingCostsInBudget;
        this.includeQBCostsInProjectedCostsCalculation =
            data.includeQBCostsInProjectedCostsCalculation ?? false;
        // Job linking only
        this.allowJobsUnderCustomer = data.allowJobsUnderCustomer;
        this.jobLinkType = data.jobLinkType ?? null;
        this.jobLinkPreference = data.jobLinkPreference ?? null;
        this.externalAccountingLink = data.externalAccountingLink;
        this.contacts =
            data.contacts?.map(
                (contact) =>
                    new BTPrimaryEntityOption(
                        contact.displayName ?? "",
                        contact.orgLinkId?.toString(),
                    ),
            ) ?? undefined;
    }

    accountingIntegrationType: AccountingIntegrationType;
    accountingIntegrationTypeName: string;
    accountingNameForLogin: string;
    canViewAccountingTab: boolean;
    isAccountConnected: boolean;
    isUserLinked: boolean;
    isEmployeePending: boolean;
    linkedToAccountingSystemImage: string;
    syncSubToVendorImgSrc: string;
    payrollItems?: BTSelectItem[];
    defaultPayrollItem: string | null;
    primaryAccountingTerm: string;
    secondaryAccountingTerm: string | null;
    tertiaryAccountingTerm: string | null;
    projectsAccountingTerm: string | null;
    primaryAccountingName: string | null;
    secondaryAccountingName: string | null;
    clientAccountingName: string | null;
    jobLinkType: JobLinkingTypes | null;
    jobLinkPreference: JobLinkingTypes | null;
    displayName: string;
    entityTerm: string;
    otherAccountingCostToolTipMsg: string | null;
    showOtherAccountingCosts: boolean;
    hasInvoicedOtherAccountingCosts: boolean;
    includeOtherAccountingCostsInBudget: boolean;
    includeQBCostsInProjectedCostsCalculation: boolean;
    allowJobsUnderCustomer: boolean;
    externalAccountingLink?: string;
    contacts?: BTPrimaryEntityOption[];
}

export class LinkedAccountingEntityResponse {
    constructor(data: typeof LinkedEntityExample) {
        this.accountingLinkedUser = new AccountingUserViewModel(data.accountingLinkedUser);
        this.canViewAccountingTab = data.canViewAccountingTab;
        this.createNewAccountingEntityOption = data.createNewAccountingEntityOption;
        this.linkingOptions =
            data.linkingOptions === null
                ? []
                : data.linkingOptions.map((d: unknown) => new BTSelectItem(d));
        this.primaryAccountingOptions =
            data.primaryAccountingOptions?.map(
                (option) => new BTSelectItem<PrimaryAccountingOptionsExtraData>(option),
            ) ?? [];
        this.secondaryAccountingOptions =
            data.secondaryAccountingOptions?.map(
                (option) => new BTSelectItem<SecondaryAccountingOptionsExtraData>(option),
            ) ?? [];
        this.tertiaryAccountingOptions =
            data.tertiaryAccountingOptions?.map((option) => new BTSelectItem(option)) ?? [];
        this.showEntityTypeHelperText = data.showEntityTypeHelperText;
    }

    accountingLinkedUser: AccountingUserViewModel;
    canViewAccountingTab: boolean;
    linkingOptions: BTSelectItem<IEntityExtraData>[];
    createNewAccountingEntityOption: LinkingAction;
    primaryAccountingOptions: BTSelectItem<PrimaryAccountingOptionsExtraData>[];
    secondaryAccountingOptions: BTSelectItem<SecondaryAccountingOptionsExtraData>[];
    tertiaryAccountingOptions: BTSelectItem[];
    showEntityTypeHelperText?: boolean;
}
